import { ApplicationContext } from '@wix/editor-platform-contexts';
import {
  BaseError,
  createErrorBuilder,
} from '@wix/public-editor-platform-errors';
import { AccessToken } from '@wix/public-editor-platform-interfaces';

export enum EditorPlatformSDKAuthErrorCode {
  EmptyAppAuthInstance = 'EmptyAppAuthInstance',
  EmptyAppDefinitionId = 'EmptyAppDefinitionId',
}

class EditorPlatformSDKAuthError extends BaseError<EditorPlatformSDKAuthErrorCode> {
  state: Partial<{
    appDefinitionId: string;
  }> = {};

  constructor(message: string, code: EditorPlatformSDKAuthErrorCode) {
    super(message, code, 'Auth Strategy Error');
  }

  withAppDefinitionId(appDefinitionId: string) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
}

export const createEditorPlatformSDKAuthError = createErrorBuilder<
  EditorPlatformSDKAuthErrorCode,
  EditorPlatformSDKAuthError
>(EditorPlatformSDKAuthError);

function hasEnoughTimeUntilExpiry(
  expires: Date | undefined,
  expiryBuffer: number,
) {
  const now = Date.now();
  const expiryTime = expires ? new Date(expires).getTime() : null;
  return expiryTime && expiryTime - now > expiryBuffer;
}

const ACCESS_TOKEN_EXPIRY_GRACE_PERIOD = 30 * 60 * 1000; // half an hour

/**
 * TODO: find a way to reuse implementation from PlatformInfra.getSDKAuth(...)
 * probably we should do it along with effort to support SDK shapes as placeholders,
 * when implementation is always injected from the main thread
 */
export const auth = () => {
  let instance: AccessToken;

  return {
    getAuthHeaders: async () => {
      const context = await ApplicationContext.getInstance();
      const appDefinitionId = context.getAppDefinitionId();
      const privateAPI = context.getPrivateAPI();

      if (!appDefinitionId) {
        throw createEditorPlatformSDKAuthError(
          EditorPlatformSDKAuthErrorCode.EmptyAppDefinitionId,
        );
      }

      if (
        !instance ||
        hasEnoughTimeUntilExpiry(
          instance.expires,
          ACCESS_TOKEN_EXPIRY_GRACE_PERIOD,
        )
      ) {
        instance = await privateAPI.auth.getAccessToken(
          appDefinitionId,
          await privateAPI.info.getMetaSiteId(),
        );
      }

      if (!instance?.token) {
        console.error(
          createEditorPlatformSDKAuthError(
            EditorPlatformSDKAuthErrorCode.EmptyAppAuthInstance,
          ).withAppDefinitionId(appDefinitionId),
        );

        return {
          headers: {
            Authorization: '',
          },
        };
      }

      return {
        headers: {
          Authorization: instance.token,
        },
      };
    },
  };
};
